import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Checkbox, FormControlLabel, LinearProgress, Tooltip, Typography } from '@mui/material';
import { SoldSummary } from 'lib/models/listSoldProducts';
import { formatNumber, price } from 'lib/numbers';

type Props = {
	summary?: SoldSummary;
	loading?: boolean;
};

export default function SoldSummaryBlock({ summary, loading }: Props) {
	const { t } = useTranslation();
	// What was consumed with a card is not Mute's revenue: subtracted only on screen
	const [discountMuteCard, setDiscountMuteCard] = useState(false);
	const shown = (total?: number, muteCardPart?: number) =>
		(total ?? 0) - (discountMuteCard ? muteCardPart ?? 0 : 0);

	return (
		<Box sx={{ mb: 2 }}>
			<Typography variant="subtitle1" sx={{ mb: 1 }}>
				<strong>Resumen</strong> (aplicando los filtros de fecha, estado, usuario interno y locación)
			</Typography>
			<Box sx={{ p: 2, borderRadius: 1, border: '1px solid', borderColor: 'grey.300', backgroundColor: 'grey.50' }}>
				<Box
					sx={{
						display: 'flex',
						flexWrap: 'wrap',
						alignItems: 'center',
						justifyContent: 'space-between',
						columnGap: 2,
						mb: 2,
					}}
				>
					<Typography variant="body2">
						Cantidad de órdenes:{' '}
						<strong>{formatNumber(shown(summary?.totalOrders, summary?.nfcCardOrders), 0)}</strong>
					</Typography>
					<Tooltip title={t('nfc:muteCardDiscountHelp')}>
						<FormControlLabel
							control={
								<Checkbox
									size="small"
									checked={discountMuteCard}
									onChange={event => setDiscountMuteCard(event.target.checked)}
								/>
							}
							label={<Typography variant="body2">{t('nfc:muteCardDiscount')}</Typography>}
						/>
					</Tooltip>
				</Box>

				<Box
					sx={{
						display: 'grid',
						gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
						gap: 2,
					}}
				>
					<Tooltip title="Incluye productos cobrados (pendientes + entregados)">
						<Typography
							variant="body2"
							component="span"
							sx={{
								display: 'inline-flex',
								alignItems: 'center',
								gap: 0.5,
								borderBottom: '1px dashed',
								borderColor: 'grey.400',
								cursor: 'help',
								width: 'fit-content',
							}}
						>
							Cantidad de productos vendidos:{' '}
							<strong>
								{formatNumber(shown(summary?.totalProductsSold, summary?.nfcCardProductsSold), 0)}
							</strong>
						</Typography>
					</Tooltip>
					<Typography variant="body2">
						Cantidad de productos entregados:{' '}
						<strong>
							{formatNumber(shown(summary?.totalDeliveredProducts, summary?.nfcCardDeliveredProducts), 0)}
						</strong>
					</Typography>
					<Typography variant="body2">
						Cantidad de productos devueltos:{' '}
						<strong>
							{formatNumber(shown(summary?.totalReturnedProducts, summary?.nfcCardReturnedProducts), 0)}
						</strong>
					</Typography>
					<Typography variant="body2">
						Total vendido: <strong>{price(shown(summary?.totalPaid, summary?.nfcCardTotalPaid))}</strong>
					</Typography>
					<Typography variant="body2">
						Total entregado:{' '}
						<strong>{price(shown(summary?.totalDelivered, summary?.nfcCardTotalDelivered))}</strong>
					</Typography>
					<Typography variant="body2">
						Total devuelto: <strong>{price(shown(summary?.totalReturned, summary?.nfcCardTotalReturned))}</strong>
					</Typography>
					<Typography variant="body2">
						Total costo de servicio:{' '}
						<strong>{price(shown(summary?.totalSaleFee, summary?.nfcCardTotalSaleFee))}</strong>
					</Typography>
					<Typography variant="body2">
						Total descuento:{' '}
						<strong>{price(shown(summary?.totalSaleDiscount, summary?.nfcCardTotalSaleDiscount))}</strong>
					</Typography>
					<Typography variant="body2" color="secondary">
						{t('nfc:muteCard')}: <strong>{price(summary?.nfcCardTotalPaid ?? 0)}</strong> (
						{formatNumber(summary?.nfcCardOrders ?? 0, 0)} órdenes,{' '}
						{formatNumber(summary?.nfcCardProductsSold ?? 0, 0)} productos)
					</Typography>

					{loading && <LinearProgress />}
				</Box>
			</Box>
		</Box>
	);
}
